  const CATALOG = Object.freeze([
    { id: 'summary', group: 'Analysis', label: 'Summary', route: 'analysis', tab: '1' },
    { id: 'trends', group: 'Analysis', label: 'Performance Trends', route: 'analysis', tab: '2' },
    { id: 'dividends', group: 'Analysis', label: 'Dividends', route: 'analysis', tab: '3' },
    { id: 'comparison', group: 'Analysis', label: 'Comparison', route: 'analysis', tab: '4' },
    { id: 'instructions', group: 'Analysis', label: 'Timely Disclosure', route: 'analysis', tab: '5' },
    { id: 'indicators', group: 'Analysis', label: 'Investment Indicators', route: 'analysis', tab: '6' },
    { id: 'world', group: 'Indices', label: 'World', route: 'indices', tab: '1' },
    { id: 'asia', group: 'Indices', label: 'Asia', route: 'indices', tab: '2' },
    { id: 'login', group: 'Login', label: 'Original widget login ↗', route: 'login' },
  ].map(Object.freeze));
  const SECTIONS = Object.freeze(['annual', 'quarterly', 'balance', 'cashflow']);
  const INVESTMENT_SECTIONS = Object.freeze([
    'investment-basic', 'investment-consensus', 'investment-finance',
    'investment-stock-attributes', 'investment-technical', 'investment-technical-indicators',
  ]);
  const normalize = (value) => {
    if (typeof value !== 'string') return '';
    const result = value.trim();
    return ['', 'null', 'undefined', '""', "''"].includes(result.toLowerCase()) ? '' : result;
  };
  const isLoopback = (url) => ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  const safeEndpoint = (value) => {
    const url = new URL(value);
    if ((url.protocol !== 'https:' && !(url.protocol === 'http:' && isLoopback(url))) ||
        url.username || url.password || url.search || url.hash) throw new Error('Invalid endpoint configuration.');
    return url;
  };
  function validateConfig(config, parentUrl) {
    const parent = safeEndpoint(new URL('/', parentUrl).href);
    if (!isLoopback(parent) && !config.parentOrigins.includes(parent.origin)) {
      throw new Error('This host is not enabled. Use the local QA server or the authorized internal host.');
    }
    if (!Array.isArray(config.profiles) || !config.profiles.length) throw new Error('No test profiles configured.');
    const ids = new Set();
    const profiles = config.profiles.filter((profile) => !profile.localOnly || isLoopback(parent)).map((profile) => {
      if (!/^[a-z0-9-]+$/.test(profile.id) || ids.has(profile.id)) throw new Error('Invalid profile configuration.');
      ids.add(profile.id);
      const base = safeEndpoint(profile.widgetBase);
      safeEndpoint(profile.loginUrl);
      if (!base.pathname.endsWith('/')) throw new Error('Widget app base must end with a slash.');
      return Object.freeze({ ...profile });
    });
    if (!profiles.length) throw new Error('No profiles available on this host.');
    for (const key of ['loginTimeoutMs', 'connectionTimeoutMs']) {
      if (!Number.isFinite(config[key]) || config[key] <= 0) throw new Error('Invalid timeout configuration.');
    }
    return Object.freeze({ ...config, profiles: Object.freeze(profiles) });
  }
  const getScreen = (id) => CATALOG.find((entry) => entry.id === id);
  const available = (screen, market) => !(screen.id === 'instructions' && normalize(market).toUpperCase() === 'US');
  function buildUrl(config, profileId, screenId, context, token) {
    const profile = config.profiles.find((item) => item.id === profileId);
    const screen = getScreen(screenId);
    if (!profile || !screen) throw new Error('Choose a configured profile and screen.');
    const market = normalize(context.mkt).toUpperCase();
    const ticker = normalize(context.ticker);
    if (screen.route !== 'login' && !normalize(token)) throw new Error('Sign in before loading a widget.');
    if (screen.route === 'analysis' && (!ticker || !['JP', 'US'].includes(market))) {
      throw new Error('Analysis requires a ticker and market JP or US.');
    }
    if (!available(screen, market)) throw new Error('Timely Disclosure is unavailable for US instruments.');
    const url = new URL(screen.route, safeEndpoint(profile.widgetBase));
    for (const key of ['ric', 'ticker', 'mkt', 'locale', 'theme']) {
      const value = key === 'mkt' ? market : normalize(context[key]);
      if (value) url.searchParams.set(key, value);
    }
    if (screen.tab) url.searchParams.set('tab', screen.tab);
    if (screen.route !== 'login') url.searchParams.set('token', normalize(token));
    // Login deliberately gets no session token, and no arbitrary context keys.
    return url;
  }
  function parseMessage(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const exact = (keys) => Object.keys(data).length === keys.length && keys.every((key) => Object.hasOwn(data, key));
    const tab = Number.isInteger(data.tab_id) && data.tab_id >= 1 && data.tab_id <= 6;
    if (!tab) return null;
    if (data.action === 'heightchange' && exact(['tab_id', 'action', 'height']) &&
        typeof data.height === 'number' && Number.isFinite(data.height) && data.height > 0) {
      return { tab_id: data.tab_id, action: 'heightchange', height: data.height };
    }
    if (data.type === 'asp_tab_change' && exact(['type', 'tab_id'])) {
      return { type: 'asp_tab_change', tab_id: data.tab_id };
    }
    if (data.type === 'asp_section_jump' &&
        ((data.tab_id === 2 && SECTIONS.includes(data.section)) ||
         (data.tab_id === 6 && INVESTMENT_SECTIONS.includes(data.section))) &&
        exact(['type', 'tab_id', 'section', 'offsetTop']) &&
        typeof data.offsetTop === 'number' && Number.isFinite(data.offsetTop) && data.offsetTop >= 0) {
      return { type: 'asp_section_jump', tab_id: data.tab_id, section: data.section, offsetTop: data.offsetTop };
    }
    return null;
  }

export { CATALOG, normalize, validateConfig, getScreen, available, buildUrl, parseMessage };
